/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
// Disabling the above a11y rules as we are only tracking clicks for analytics, not for interaction
// and the div is not interactive otherwise

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withPolaris } from 'Components/protons/Polaris';
import { Button } from 'Components/atoms/Button';
import { Image } from 'Components/atoms/Image';
import './_style.scss';

const SlideshowCarousel = ({
  media,
  goToIndex,
  onIndexChange,
  context: { cssPrefix },
}) => {
  if (!media || media.length === 0) return null;

  // Ensures that any given index is within the valid bounds of the media array.
  const clampIndex = (index) => Math.max(0, Math.min(index, media.length - 1));

  // The currently visible slide. Initialize with a clamped index to handle out-of-range initial values.
  const [currentIndex, setCurrentIndex] = useState(clampIndex(goToIndex));
  // State to track if scrolling animation is active
  const [isScrolling, setIsScrolling] = useState(false);
  // Ref for the scrollable container
  const scrollContainerRef = useRef(null);
  // Refs for individual slide elements
  const slideRefs = useRef([]);

  const scrollToSlide = (targetIndex) => {
    const slideElement = slideRefs.current[targetIndex];
    const container = scrollContainerRef.current;

    if (slideElement && container) {
      setIsScrolling(true);

      // Calculate the exact scroll position for faster, more precise scrolling
      const slideOffsetLeft = slideElement.offsetLeft;
      const containerWidth = container.clientWidth;
      const slideWidth = slideElement.clientWidth;
      const targetScrollLeft = slideOffsetLeft - (containerWidth - slideWidth) / 2;

      // Use a faster custom scroll animation
      const startScrollLeft = container.scrollLeft;
      const distance = targetScrollLeft - startScrollLeft;
      const duration = 10;
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-out function for smooth deceleration
        const easeOut = 1 - (1 - progress) ** 3;

        container.scrollLeft = startScrollLeft + (distance * easeOut);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          setIsScrolling(false);
        }
      };

      requestAnimationFrame(animateScroll);

      // Update current index and notify parent
      setCurrentIndex(targetIndex);
      onIndexChange(targetIndex);
    }
  };

  // This effect triggers a transition when the parent component requests a new slide via `goToIndex`.
  useEffect(() => {
    // Do not transition if there's only one slide or the index hasn't changed.
    if (media.length <= 1) return;
    const clampedGoToIndex = clampIndex(goToIndex);
    if (goToIndex !== null && goToIndex !== undefined && clampedGoToIndex !== currentIndex) {
      scrollToSlide(clampedGoToIndex);
    }
  }, [goToIndex, currentIndex, media.length]);

  const pushTracking = (buttonType) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'slideshow.button_click',
        'slideshow.element': 'carousel',
        'slideshow.buttonType': buttonType,
      });
    }
  };

  const handlePrev = () => {
    if (media.length <= 1 || isScrolling) return;
    const newIndex = (currentIndex - 1 + media.length) % media.length;
    scrollToSlide(newIndex);

    pushTracking('prev');
  };

  const handleNext = () => {
    if (media.length <= 1 || isScrolling) return;
    const newIndex = (currentIndex + 1) % media.length;
    scrollToSlide(newIndex);

    pushTracking('next');
  };

  const baseClass = `${cssPrefix}__slideshow-carousel`;

  return (
    <div className={`${baseClass}`}>
      <div
        className={`${baseClass}__scroll-container`}
        ref={scrollContainerRef}
        onClick={() => pushTracking('image')}
      >
        {media.map((mediaItem, index) => (
          <div
            key={index}
            ref={(el) => {
              slideRefs.current[index] = el;
            }}
            className={`${baseClass}__slide`}
          >
            <Image
              src={mediaItem?.large || mediaItem?.thumb}
              alt={mediaItem?.alt || ''}
              size="content-image-full"
              extraClassNames={{
                [`${baseClass}__image`]: true,
              }}
              isLCPCandidate={index === 0 ? (mediaItem.isLCPCandidate || false) : false}
              lazyLoad={index === 0 ? (mediaItem.lazyLoad || false) : true}
            />
          </div>
        ))}
      </div>
      <div
        className={`${baseClass}__counter`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-label={`Image ${currentIndex + 1} of ${media.length}`}
      >
        <span className={`${baseClass}__counter-text`}>
          <strong>{currentIndex + 1}</strong>
          {` of ${media.length}`}
        </span>
        <span
          className={`${baseClass}__counter-icon`}
          aria-hidden="true"
        />
      </div>
      <Button
        loadStyles={false}
        extraClassNames={{
          [`${baseClass}__button`]: true,
          [`${baseClass}__button--prev`]: true,
        }}
        onClick={handlePrev}
        srLabel="Previous image"
        disabled={isScrolling}
      />
      <Button
        loadStyles={false}
        extraClassNames={{
          [`${baseClass}__button`]: true,
          [`${baseClass}__button--next`]: true,
        }}
        onClick={handleNext}
        srLabel="Next image"
        disabled={isScrolling}
      />
    </div>
  );
};

SlideshowCarousel.propTypes = {
  media: PropTypes.arrayOf(PropTypes.shape({
    thumb: PropTypes.string,
    large: PropTypes.string,
    caption: PropTypes.string,
    credits: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isLCPCandidate: PropTypes.bool,
    lazyLoad: PropTypes.bool,
  })).isRequired,
  goToIndex: PropTypes.number,
  onIndexChange: PropTypes.func,
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }).isRequired,
};

SlideshowCarousel.defaultProps = {
  goToIndex: 0,
  onIndexChange: () => {},
};

export default withPolaris(SlideshowCarousel);
